import React, { useEffect, useMemo, useState } from "react";
import { MdDragIndicator } from "react-icons/md";
import { buildApiUrl } from "utils/api";
import { getToken } from "utils/auth";
import { usePermission } from "hooks/usePermission";
import routes from "routes.js";

let sharedMobileOrderIds = null;
const mobileOrderSubscribers = new Set();

const subscribeMobileOrder = (callback) => {
  mobileOrderSubscribers.add(callback);
  return () => mobileOrderSubscribers.delete(callback);
};

const setSharedMobileOrder = (orderIds) => {
  sharedMobileOrderIds = orderIds;
  mobileOrderSubscribers.forEach((callback) => callback(orderIds));
};

const routeModuleMap = {
  dashboard: "dashboard",
  calendar: "calendar",
  "careof-management": "careof_management",
  "lead-management": "lead_management",
  "client-management": "client_management",
  "staff-management": "staff_management",
  "folders/leads": "lead_folder",
  "folders/clients": "lead_folder"
};

export const menuIdToRoutePath = {
  main_dashboard: "dashboard",
  calendar: "calendar",
  careof_management: "careof-management",
  lead_management: "lead-management",
  client_management: "client-management",
  lead_folder: "folders/leads",
  client_folder: "folders/clients",
  user_profile: "profile",
  staff_management: "staff-management",
  menu_order: "menu-order",
  user_manual: "user-manual"
};

export const routePathToMenuId = Object.keys(menuIdToRoutePath).reduce((acc, id) => {
  acc[menuIdToRoutePath[id]] = id;
  return acc;
}, {});

export const labelMap = {
  dashboard: "Dashboard",
  calendar: "Calendar",
  "careof-management": "CareOf",
  "lead-management": "Leads",
  "client-management": "Clients",
  "staff-management": "Staff",
  "folders/leads": "Lead Folders",
  "folders/clients": "Client Folders",
};

export const getRouteLabel = (route) => labelMap[route.path] || route.name;

export const getVisibleRoutes = (accessible) =>
  routes.filter((route) => {
    if (route.layout !== "/admin" || route.hidden || route.excludeFromMenuOrder || !route.icon) return false;
    const moduleKey = routeModuleMap[route.path];
    if (moduleKey && !accessible.includes(moduleKey)) return false;
    return true;
  });

const getRoutePathFromOrderId = (orderId, visibleRoutes) => {
  if (!orderId) return null;
  if (visibleRoutes.some((route) => route.path === orderId)) return orderId;
  return menuIdToRoutePath[orderId] || null;
};

const buildOrderedRoutes = (visibleRoutes, mobileOrderIds) => {
  if (!Array.isArray(mobileOrderIds) || mobileOrderIds.length === 0) {
    return visibleRoutes;
  }

  const routeByPath = new Map(visibleRoutes.map((route) => [route.path, route]));
  const ordered = [];

  mobileOrderIds.forEach((orderId) => {
    const path = getRoutePathFromOrderId(orderId, visibleRoutes);
    if (path) {
      const route = routeByPath.get(path);
      if (route) ordered.push(route);
    }
  });

  visibleRoutes.forEach((route) => {
    if (!ordered.some((item) => item.path === route.path)) ordered.push(route);
  });

  return ordered;
};

export const useMobileNavigationOrder = () => {
  const { getAccessible } = usePermission();
  const accessible = useMemo(() => getAccessible(), [getAccessible]);
  const [mobileOrderIds, setMobileOrderIds] = useState(sharedMobileOrderIds);
  const [saving, setSaving] = useState(false);

  const visibleRoutes = useMemo(() => getVisibleRoutes(accessible), [accessible]);
  const orderedRoutes = useMemo(() => buildOrderedRoutes(visibleRoutes, mobileOrderIds), [visibleRoutes, mobileOrderIds]);

  useEffect(() => {
    const unsubscribe = subscribeMobileOrder((orderIds) => {
      setMobileOrderIds(orderIds);
    });
    return unsubscribe;
  }, []);

  const updateMobileOrderIds = (orderIds) => {
    setMobileOrderIds(orderIds);
    setSharedMobileOrder(orderIds);
  };

  useEffect(() => {
    let mounted = true;
    const fetchOrder = async () => {
      try {
        const token = getToken();
        const res = await fetch(buildApiUrl("/api/user/mobile-navigation"), {
          method: "GET",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!mounted || !res.ok) return;
        const data = await res.json();
        const order =
          Array.isArray(data?.menuOrder) ? data.menuOrder :
          Array.isArray(data?.mobileNavigationOrder) ? data.mobileNavigationOrder :
          Array.isArray(data?.data?.menuOrder) ? data.data.menuOrder :
          Array.isArray(data?.data?.mobileNavigationOrder) ? data.data.mobileNavigationOrder :
          null;
        if (order) updateMobileOrderIds(order);
      } catch (e) {
        // ignore and fallback to default route order
      }
    };
    fetchOrder();
    return () => {
      mounted = false;
    };
  }, []);

  const saveMobileOrder = async (orderIds) => {
    setSaving(true);
    try {
      const token = getToken();
      const res = await fetch(buildApiUrl("/api/user/mobile-navigation"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ menuOrder: orderIds }),
      });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      const order =
        Array.isArray(data?.mobileNavigationOrder) ? data.mobileNavigationOrder :
        Array.isArray(data?.menuOrder) ? data.menuOrder :
        Array.isArray(data?.data?.mobileNavigationOrder) ? data.data.mobileNavigationOrder :
        Array.isArray(data?.data?.menuOrder) ? data.data.menuOrder :
        null;
      if (order) {
        updateMobileOrderIds(order);
      } else {
        updateMobileOrderIds(orderIds);
      }
      const successMessage = data?.message || "Menu order saved successfully";
      if (typeof window !== "undefined" && typeof window.__showToast === "function") {
        window.__showToast(successMessage, "success");
      }
    } catch (e) {
      if (typeof window !== "undefined" && typeof window.__showToast === "function") {
        window.__showToast(e.message || "Failed to save menu order", "error");
      }
    } finally {
      setSaving(false);
    }
  };

  return {
    visibleRoutes,
    orderedRoutes,
    mobileOrderIds,
    setMobileOrderIds: updateMobileOrderIds,
    saving,
    saveMobileOrder,
  };
};

export const MenuOrderSettingsPanel = ({
  mobileOrderIds,
  visibleRoutes,
  onOrderChange,
  onSave,
  onClose,
  saving,
}) => {
  const [draggedMenuId, setDraggedMenuId] = useState(null);
  const [touchHoverMenuId, setTouchHoverMenuId] = useState(null);

  const getMenuIdFromElement = (element) => {
    if (!element || typeof element.closest !== "function") return null;
    const menuItem = element.closest("[data-menu-id]");
    return menuItem?.getAttribute("data-menu-id") || null;
  };

  const displayRoutes = (
    Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0
      ? mobileOrderIds
          .map((id) => getRoutePathFromOrderId(id, visibleRoutes))
          .map((path) => visibleRoutes.find((route) => route.path === path))
          .filter(Boolean)
      : visibleRoutes
  );

  const moveRoute = (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const currentIds = Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0
      ? [...mobileOrderIds]
      : visibleRoutes.map((route) => routePathToMenuId[route.path]).filter(Boolean);
    const next = [...currentIds];
    const [movedItem] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, movedItem);
    onOrderChange(next);
  };

  return (
    <div className="rounded-3xl border border-gray-200 bg-gray-50 p-3 text-xs text-gray-500 dark:border-white/10 dark:bg-navy-800 dark:text-gray-300">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-navy-700 dark:text-white">Menu order</p>
          <p className="text-[11px] text-gray-500 dark:text-gray-400">Drag the items to reorder the user menu.</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="rounded-full border border-gray-200 bg-white px-2 py-1 text-xs text-gray-600 transition hover:bg-gray-100 dark:border-white/10 dark:bg-navy-900 dark:text-gray-300"
        >
          Close
        </button>
      </div>
      <div className="space-y-2">
        {displayRoutes.map((route, idx) => {
          const menuId = routePathToMenuId[route.path];
          return (
            <div
              key={route.path}
              data-menu-id={menuId}
              draggable
              onDragStart={() => setDraggedMenuId(menuId)}
              onDragEnd={() => {
                setDraggedMenuId(null);
                setTouchHoverMenuId(null);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setTouchHoverMenuId(menuId);
              }}
              onDrop={() => {
                const currentIds = Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0
                  ? [...mobileOrderIds]
                  : visibleRoutes.map((r) => routePathToMenuId[r.path]).filter(Boolean);
                const fromIndex = currentIds.indexOf(draggedMenuId);
                const toIndex = currentIds.indexOf(menuId);
                moveRoute(fromIndex, toIndex);
              }}
              onTouchStart={() => setDraggedMenuId(menuId)}
              onTouchMove={(event) => {
                const touch = event.touches[0];
                if (!touch) return;
                const element = document.elementFromPoint(touch.clientX, touch.clientY);
                const targetMenuId = element ? getMenuIdFromElement(element) : null;
                setTouchHoverMenuId(targetMenuId);
              }}
              onTouchEnd={(event) => {
                const touch = event.changedTouches[0];
                if (!touch) {
                  setDraggedMenuId(null);
                  setTouchHoverMenuId(null);
                  return;
                }
                const element = document.elementFromPoint(touch.clientX, touch.clientY);
                const dropMenuId = element ? getMenuIdFromElement(element) : null;
                const currentIds = Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0
                  ? [...mobileOrderIds]
                  : visibleRoutes.map((r) => routePathToMenuId[r.path]).filter(Boolean);
                const fromIndex = currentIds.indexOf(draggedMenuId);
                const toIndex = currentIds.indexOf(dropMenuId);
                moveRoute(fromIndex, toIndex);
                setDraggedMenuId(null);
                setTouchHoverMenuId(null);
              }}
              style={{ touchAction: "none" }}
              className={`flex cursor-grab items-center justify-between gap-2 rounded-2xl border px-3 py-2 text-sm transition focus:outline-none dark:border-white/10 dark:bg-navy-900 ${
                draggedMenuId === menuId || touchHoverMenuId === menuId
                  ? "bg-brand-50 dark:bg-brand-500/10"
                  : "bg-white dark:bg-navy-900"
              }`}
            >
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-white/5 dark:text-gray-300">
                  <MdDragIndicator className="h-4 w-4" />
                </span>
                <span className="font-medium text-gray-800 dark:text-white">{getRouteLabel(route)}</span>
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">{idx + 1}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => {
            const ids = Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0
              ? mobileOrderIds
              : visibleRoutes.map((route) => routePathToMenuId[route.path]).filter(Boolean);
            onSave(ids);
          }}
          disabled={saving}
          className="rounded bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {saving ? "Saving..." : "Save order"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-100 dark:border-white/10 dark:bg-navy-900 dark:text-gray-300 dark:hover:bg-navy-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};
